import { useCallback, useEffect, useMemo, useState } from 'react'
import { Navigate, useNavigate } from 'react-router'
import { useAuth } from '../contexts/AuthContext'
import { ApiError } from '../services/BaseApiService'
import { auditorApiService, saveBlob } from '../services/auditorApiService'
import type { AuditMe, AuditorRequest, ControlCalendarRow, PolicySummary, PopulationRow } from '../services/auditorApiService'
import { colors, fontSize, radius, spacing } from '../styles/theme'

type Tab = 'calendar' | 'populations' | 'requests' | 'policies'
const TABS: { id: Tab; label: string }[] = [
  { id: 'calendar', label: 'Control operation' },
  { id: 'populations', label: 'Populations & samples' },
  { id: 'requests', label: 'Requests' },
  { id: 'policies', label: 'Policies' },
]
const STATUS_COLOR: Record<string, string> = {
  'on-time': colors.success, late: colors.warning, waived: colors.textSecondary, open: colors.textSecondary,
  missing: colors.error, overdue: colors.error, failed: colors.error,
}
const card: React.CSSProperties = { background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: radius.md, padding: spacing.md }
const btn: React.CSSProperties = { padding: `${spacing.xs} ${spacing.md}`, background: colors.primary, color: '#fff', border: 'none', borderRadius: radius.sm, cursor: 'pointer' }
const ghost: React.CSSProperties = { ...btn, background: 'transparent', color: colors.text, border: `1px solid ${colors.border}` }
const day = (iso: string | null) => (iso ? iso.slice(0, 10) : '—')
const iso = (d: string, endOfDay = false) => `${d}T${endOfDay ? '23:59:59' : '00:00:00'}Z`

/**
 * Read-only SOC 2 Type II auditor portal. Everything is scoped to the observation window chosen at the
 * top; every view and download is logged server-side (audit.access).
 */
export default function AuditorPortalPage() {
  const { user, loading, logout } = useAuth()
  const navigate = useNavigate()
  const [me, setMe] = useState<AuditMe | null>(null)
  const [tab, setTab] = useState<Tab>('calendar')
  const [since, setSince] = useState(() => new Date(Date.now() - 90 * 864e5).toISOString().slice(0, 10))
  const [until, setUntil] = useState(() => new Date().toISOString().slice(0, 10))
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)

  useEffect(() => { if (user) auditorApiService.me().then(setMe).catch(e => setError(e instanceof ApiError ? e.message : 'Access denied')) }, [user])

  if (loading) return null
  if (!user) return <Navigate to="/login" replace />

  const S = iso(since), U = iso(until, true)
  async function download(fn: () => Promise<Blob>, name: string) {
    setBusy(true); setError('')
    try { saveBlob(await fn(), name) } catch (e) { setError(e instanceof ApiError ? e.message : 'Download failed') } finally { setBusy(false) }
  }

  return (
    <div style={{ minHeight: '100vh', background: colors.background, padding: spacing.lg }}>
      <div style={{ maxWidth: 1100, margin: '0 auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: spacing.sm }}>
          <div>
            <h1 style={{ fontSize: fontSize.xl, fontWeight: 700, color: colors.text, margin: 0 }}>DocMorphia SOC 2 evidence</h1>
            <div style={{ fontSize: fontSize.sm, color: colors.textSecondary }}>
              {me?.preview ? 'Super-admin preview of the auditor portal' : `${me?.firm ?? ''} · read-only access until ${day(me?.expiresAt ?? null)} · all activity is logged`}
            </div>
          </div>
          <button style={ghost} onClick={async () => { await logout(); navigate('/login') }}>Sign out</button>
        </div>

        <div style={{ ...card, display: 'flex', gap: spacing.sm, alignItems: 'center', flexWrap: 'wrap', margin: `${spacing.md} 0` }}>
          <strong style={{ fontSize: fontSize.sm }}>Observation window</strong>
          <label style={{ fontSize: fontSize.sm }}>From <input type="date" value={since} onChange={e => setSince(e.target.value)} /></label>
          <label style={{ fontSize: fontSize.sm }}>To <input type="date" value={until} onChange={e => setUntil(e.target.value)} /></label>
          <button style={btn} disabled={busy} onClick={() => download(() => auditorApiService.packageZip(S, U), `docmorphia-soc2-evidence-${since}-${until}.zip`)}>
            Download full evidence package
          </button>
        </div>
        {error && <div role="alert" style={{ color: colors.error, marginBottom: spacing.sm }}>{error}</div>}

        <nav style={{ display: 'flex', gap: spacing.xs, borderBottom: `1px solid ${colors.border}`, marginBottom: spacing.md, flexWrap: 'wrap' }}>
          {TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} style={{
              padding: `${spacing.sm} ${spacing.md}`, background: 'none', border: 'none', cursor: 'pointer',
              borderBottom: `2px solid ${tab === t.id ? colors.primary : 'transparent'}`, fontWeight: tab === t.id ? 600 : 400, color: colors.text,
            }}>{t.label}</button>
          ))}
        </nav>

        {tab === 'calendar' && <CalendarTab since={S} until={U} />}
        {tab === 'populations' && me && <PopulationsTab since={S} until={U} categories={me.populations} download={download} />}
        {tab === 'requests' && <RequestsTab canCreate={!me?.preview} download={download} />}
        {tab === 'policies' && <PoliciesTab />}
      </div>
    </div>
  )
}

function useLoad<T>(fn: () => Promise<T>, deps: unknown[]) {
  const [data, setData] = useState<T | null>(null)
  const [error, setError] = useState('')
  const reload = useCallback(() => { setError(''); fn().then(setData).catch(e => setError(e instanceof ApiError ? e.message : 'Failed to load')) }, deps) // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { reload() }, [reload])
  return { data, error, reload }
}

function CalendarTab({ since, until }: { since: string; until: string }) {
  const { data, error } = useLoad(() => auditorApiService.calendar(since, until), [since, until])
  const [open, setOpen] = useState<string | null>(null)
  if (error) return <div role="alert" style={{ color: colors.error }}>{error}</div>
  if (!data) return <div style={{ color: colors.textSecondary }}>Loading…</div>
  return (
    <div style={{ ...card, padding: 0, overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: fontSize.sm }}>
        <thead><tr style={{ textAlign: 'left', color: colors.textSecondary }}>
          {['Control', 'Criteria', 'Cadence', 'Operated', 'Exceptions'].map(h => <th key={h} style={{ padding: spacing.sm }}>{h}</th>)}
        </tr></thead>
        <tbody>
          {data.map(c => {
            const exceptions = c.occurrences.filter(o => ['missing', 'overdue', 'failed', 'late'].includes(o.status))
            return [
              <tr key={c.controlKey} style={{ borderTop: `1px solid ${colors.border}`, cursor: 'pointer' }} onClick={() => setOpen(open === c.controlKey ? null : c.controlKey)}>
                <td style={{ padding: spacing.sm }}><strong>{c.controlKey}</strong> · {c.title}</td>
                <td style={{ padding: spacing.sm, color: colors.textSecondary }}>{c.criteria}</td>
                <td style={{ padding: spacing.sm, color: colors.textSecondary }}>{c.kind === 'automated' ? 'Automated' : c.frequency}</td>
                <td style={{ padding: spacing.sm }}>{c.met} / {c.expected}</td>
                <td style={{ padding: spacing.sm, color: exceptions.length ? colors.error : colors.success }}>{exceptions.length || 'None'}</td>
              </tr>,
              open === c.controlKey && (
                <tr key={`${c.controlKey}-detail`}><td colSpan={5} style={{ padding: spacing.sm, background: colors.background }}>
                  {c.occurrences.length === 0 ? 'No occurrences expected in this window.' : c.occurrences.map(o => (
                    <div key={o.period} style={{ display: 'flex', gap: spacing.md, fontSize: fontSize.sm }}>
                      <span style={{ minWidth: 90 }}>{o.period}</span>
                      <span style={{ color: STATUS_COLOR[o.status] ?? colors.text, minWidth: 70 }}>{o.status}</span>
                      <span style={{ color: colors.textSecondary }}>{o.completedAt ? `completed ${day(o.completedAt)}` : `due ${day(o.dueAt)}`}{o.evidence ? ` · ${o.evidence}` : ''}</span>
                    </div>
                  ))}
                </td></tr>
              ),
            ]
          })}
        </tbody>
      </table>
    </div>
  )
}

function PopulationsTab({ since, until, categories, download }: {
  since: string; until: string; categories: string[]; download: (fn: () => Promise<Blob>, name: string) => Promise<void>
}) {
  const [category, setCategory] = useState(categories.includes('changes') ? 'changes' : categories[0])
  const { data, error } = useLoad(() => auditorApiService.population(category, since, until), [category, since, until])
  const [picked, setPicked] = useState<Set<string>>(new Set())
  const key = (r: PopulationRow) => (r.seq != null ? `s${r.seq}` : `p${r.subject}`)
  const prNumber = (r: PopulationRow) => Number(/^#(\d+)/.exec(r.subject ?? '')?.[1] ?? NaN)
  useEffect(() => setPicked(new Set()), [category, since, until])

  const pickRandom = (n: number) => {
    if (!data) return
    const shuffled = [...data].sort(() => Math.random() - 0.5).slice(0, n)
    setPicked(new Set(shuffled.map(key)))
  }
  const selection = useMemo(() => (data ?? []).filter(r => picked.has(key(r))), [data, picked])
  const seqs = selection.filter(r => r.seq != null).map(r => r.seq!)
  const prs = selection.filter(r => r.type === 'change.pr_merged').map(prNumber).filter(n => !Number.isNaN(n))

  return (
    <div>
      <div style={{ ...card, display: 'flex', gap: spacing.sm, alignItems: 'center', flexWrap: 'wrap', marginBottom: spacing.sm }}>
        <label style={{ fontSize: fontSize.sm }}>Population{' '}
          <select value={category} onChange={e => setCategory(e.target.value)}>{categories.map(c => <option key={c} value={c}>{c}</option>)}</select>
        </label>
        <span style={{ fontSize: fontSize.sm, color: colors.textSecondary }}>{data ? `${data.length} items` : ''}</span>
        <button style={ghost} onClick={() => download(() => auditorApiService.populationCsv(category, since, until), `population-${category}.csv`)}>Download CSV</button>
        <button style={ghost} onClick={() => pickRandom(25)} disabled={!data?.length}>Select random 25</button>
        <button style={btn} disabled={!selection.length} onClick={() => download(() => auditorApiService.sample(since, until, seqs, prs), `sample-${category}.zip`)}>
          Download evidence for {selection.length} selected
        </button>
      </div>
      {error && <div role="alert" style={{ color: colors.error }}>{error}</div>}
      <div style={{ ...card, padding: 0, overflowX: 'auto', maxHeight: 600, overflowY: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: fontSize.sm }}>
          <tbody>
            {(data ?? []).map(r => (
              <tr key={key(r)} style={{ borderTop: `1px solid ${colors.border}` }}>
                <td style={{ padding: spacing.xs }}>
                  <input type="checkbox" aria-label={`Select ${r.subject ?? r.seq}`} checked={picked.has(key(r))}
                    onChange={e => setPicked(p => { const n = new Set(p); if (e.target.checked) n.add(key(r)); else n.delete(key(r)); return n })} />
                </td>
                <td style={{ padding: spacing.xs, whiteSpace: 'nowrap' }}>{r.at.slice(0, 16).replace('T', ' ')}</td>
                <td style={{ padding: spacing.xs }}>{r.type}</td>
                <td style={{ padding: spacing.xs }}>{r.link ? <a href={r.link} target="_blank" rel="noreferrer">{r.subject}</a> : r.subject}</td>
                <td style={{ padding: spacing.xs, color: colors.textSecondary }}>{r.actor}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function RequestsTab({ canCreate, download }: { canCreate: boolean; download: (fn: () => Promise<Blob>, name: string) => Promise<void> }) {
  const { data, error, reload } = useLoad<AuditorRequest[]>(() => auditorApiService.requests(), [])
  const [title, setTitle] = useState('')
  const [details, setDetails] = useState('')
  return (
    <div>
      {canCreate && (
        <div style={{ ...card, marginBottom: spacing.md }}>
          <div style={{ fontWeight: 600, marginBottom: spacing.xs }}>Request evidence</div>
          <input aria-label="Request title" placeholder="e.g. Signed policy acknowledgements for 2026" value={title} onChange={e => setTitle(e.target.value)}
            style={{ width: '100%', boxSizing: 'border-box', padding: spacing.sm, marginBottom: spacing.xs }} />
          <textarea aria-label="Request details" placeholder="Details, sample IDs, period…" value={details} onChange={e => setDetails(e.target.value)} rows={3}
            style={{ width: '100%', boxSizing: 'border-box', padding: spacing.sm }} />
          <button style={{ ...btn, marginTop: spacing.xs }} disabled={!title.trim()}
            onClick={async () => { await auditorApiService.createRequest(title, details); setTitle(''); setDetails(''); reload() }}>Submit request</button>
        </div>
      )}
      {error && <div role="alert" style={{ color: colors.error }}>{error}</div>}
      {(data ?? []).map(r => (
        <div key={r.auditorRequestId} style={{ ...card, marginBottom: spacing.sm }}>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <strong>{r.title}</strong><span style={{ color: r.status === 'open' ? colors.warning : colors.success }}>{r.status}</span>
          </div>
          {r.details && <div style={{ fontSize: fontSize.sm, color: colors.textSecondary }}>{r.details}</div>}
          {r.responseNotes && <div style={{ fontSize: fontSize.sm, marginTop: spacing.xs }}>Response: {r.responseNotes}</div>}
          {r.responseFileName && <button style={{ ...ghost, marginTop: spacing.xs }}
            onClick={() => download(() => auditorApiService.requestResponse(r.auditorRequestId), r.responseFileName!)}>{r.responseFileName}</button>}
        </div>
      ))}
      {data?.length === 0 && <div style={{ color: colors.textSecondary }}>No requests yet.</div>}
    </div>
  )
}

function PoliciesTab() {
  const { data, error } = useLoad<PolicySummary[]>(() => auditorApiService.policies(), [])
  const [doc, setDoc] = useState<{ name: string; markdown: string } | null>(null)
  if (error) return <div role="alert" style={{ color: colors.error }}>{error}</div>
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(220px, 1fr) 3fr', gap: spacing.md }}>
      <div style={card}>
        {(data ?? []).map(p => (
          <div key={p.name} style={{ marginBottom: spacing.xs }}>
            <button style={{ background: 'none', border: 'none', color: colors.primary, cursor: 'pointer', padding: 0, textAlign: 'left' }}
              onClick={async () => setDoc(await auditorApiService.policy(p.name))}>{p.title}</button>
            <div style={{ fontSize: fontSize.xs ?? fontSize.sm, color: colors.textSecondary }}>effective {p.effective ?? '—'} · sha256 {p.sha256.slice(0, 12)}…</div>
          </div>
        ))}
      </div>
      <div style={card}>
        {doc ? <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', fontSize: fontSize.sm, margin: 0 }}>{doc.markdown}</pre>
          : <span style={{ color: colors.textSecondary }}>Select a policy.</span>}
      </div>
    </div>
  )
}
